import type { SetRequired } from 'type-fest';
import { v4 as uuid } from 'uuid';

import { createQcsThunks, createResourceEntityAdapter, retryAsync } from 'app-components/flux/core/resourceSliceUtils';
import { getLogger } from 'app-components/utils/core';
import { createAppSlice } from 'app-components/utils/redux-store/createAppSlice';

import buildStorageApi from './api';
import type { DocumentType, StorageDomain } from './types';

const initialState: StorageDomain = {
  entities: {},
  ids: [],
  isLoading: false,
  loadPending: true,
  error: null,
  lastSyncDate: null,
};

const log = getLogger('data/storage/slice');
const entityAdapter = createResourceEntityAdapter<DocumentType>();
export const createStorageSlice = <D extends string>(domain: D) => {
  const api = buildStorageApi(domain);
  const slice = createAppSlice({
    name: `storage/${domain}`,
    initialState: entityAdapter.getInitialState(initialState),
    reducers: (create) => ({
      ...createQcsThunks({
        api,
        create,
        adapter: entityAdapter,
        log,
        refetch(dispatch) {
          dispatch(slice.actions.fetch());
        },
      }),
      syncCdnUrl: create.asyncThunk(
        async (id: string) => {
          return { url: await retryAsync(() => api.getCdnUrl(id, true), 3, 5_000), id };
        },
        {
          fulfilled(state, action) {
            entityAdapter.updateOne(state, { id: action.payload.id, changes: action.payload });
          },
        },
      ),
      syncNoCdnUrl: create.asyncThunk(
        async (id: string) => {
          return { url: await retryAsync(() => api.getCdnUrl(id, false), 3, 5_000), id };
        },
        {
          fulfilled(state, action) {
            entityAdapter.updateOne(state, { id: action.payload.id, changes: action.payload });
          },
        },
      ),
      syncDocument: create.asyncThunk(async (id: string) => api.fetchById(id), {
        fulfilled(state, action) {
          entityAdapter.setOne(state, action.payload);
        },
      }),
      uploadNewDocument: create.asyncThunk(
        async (
          payload: SetRequired<DocumentType, 'name' | 'mimeType'> & { fileUri: string },
          thunkApi,
        ): Promise<DocumentType> => {
          const clientId = uuid();
          const createRequest = thunkApi.dispatch(
            slice.actions.create({
              name: payload.name,
              mimeType: payload.mimeType,
              ...(payload.size === undefined ? {} : { size: payload.size }),
              clientId,
            }),
          );
          const abortCreate = (): void => createRequest.abort();
          if (thunkApi.signal.aborted) {
            abortCreate();
          } else {
            thunkApi.signal.addEventListener('abort', abortCreate, { once: true });
          }
          const result = await createRequest.unwrap().finally(() => {
            thunkApi.signal.removeEventListener('abort', abortCreate);
          });
          const documentId = result.id;
          await retryAsync(() => {
            if (thunkApi.signal.aborted) {
              throw new Error('Storage document upload canceled.');
            }
            return api.upload({
              fileUri: payload.fileUri,
              name: payload.name,
              documentId,
              mimeType: payload.mimeType,
              signal: thunkApi.signal,
            });
          });
          return result;
        },
      ),
    }),
    selectors: {
      ...entityAdapter.getSelectors(),
    },
  });
  return slice;
};
